import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Fab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  Assignment,
  CallMerge,
  CallSplit,
  Edit,
  InfoOutlined,
  MoneyOffOutlined,
  PlaylistAdd,
  RemoveCircle,
  UndoRounded,
} from '@mui/icons-material'
import { Form, Formik } from 'formik'
import * as React from 'react'
import { inject } from 'mobx-react'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import CardWithTitle from '../common/CardWithTitle/CardWithTitle'
import FullWidthField from '../common/FullWidthField'
import { IProviderTypeDto } from '../generated_client'
import * as Yup from 'yup'
import QuoteDetails from './QuoteDetails'
import { createIQuoteFormValues, submitQuoteFormValues } from './QuoteFormValues'
import QuoteTotals from './QuoteTotals'
import { IStores } from 'src/Stores'
import { formatCurrency } from 'src/utils/Formatter'
import AddQuoteLineModal from './AddQuoteLineModal'
import EditQuoteLineModal from './EditQuoteLineModal'
import { IQuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import ReconcileLineItemModal from './ReconcileLineItemModal'
import { PlaylistRemove } from 'mdi-material-ui'
import ReconcileLineItemWizardModal from './ReconcileLineItemWizardModal'
import { QuoteStatus } from 'src/utils/QuoteStatus'
import { CostSourceText } from './CostSourceText'
import { QuoteLineItemType } from 'src/utils/QuoteLineItemType'
import { QuoteInboxItem } from 'src/viewModels/workflow/QuoteInboxItem'
import {
  GetAnesthesiaLineItems,
  GetMiscellaneousLineItems,
  GetPhysicalTherapyLineItems,
  GetProfessionalComponentLineItems,
  GetTechnicalComponentLineItems,
  GetClaimButtonsWithSummary,
} from 'src/services/QuoteService'
import { AppealType } from 'src/utils/AppealType'

const QuoteSchema = Yup.object().shape({
  adjustedReconciledCost: Yup.number()
    .transform(function (value, _originalValue) {
      return this.isType(value) ? value : -1
    })
    .min(0, 'Adjusted Cost must be 0 or greater')
    .max(1000000000000, 'Adjusted Cost must be 1000000000000 or less')
    .required('Reconciled Adjusted Cost is required'),
  targetReconciledMargin: Yup.number()
    .transform(function (value, _originalValue) {
      return this.isType(value) ? value : -1
    })
    .min(0, 'Target Margin must be 0% or greater')
    .max(100, 'Target Margin must be 100% or less')
    .required('Reconciled Target Margin is required'),
})

interface IReconcileQuoteRouteParams {
  quoteId?: string
}

interface IReconcileQuoteProps {
  match?: { params: IReconcileQuoteRouteParams }
  history?: any
  location?: any
  getLineItemsTotals?: (lineItems: any) => number
  getLineItemsReconciledTotal?: (lineItems: any) => number
  getProviderTypes?: () => Promise<void>
  getTypesByCategory?: (type: string) => Promise<void>
  isContractModalOpen?: boolean
  isMultpleProcedureDiscountDialogOpen?: boolean
  loadQuote?: (quoteId: string, reload?: boolean) => Promise<void>
  providerTypes?: IProviderTypeDto[]
  rerouteToPath?: (path: string) => void
  selectedQuote?: IQuoteDTO
  openReconcileModal?: (quote: IQuoteDTO, modalSwitch: string) => void
  assistantWarning?: boolean
  openReconModal?: (
    quote: IQuoteDTO,
    modalSwitch: string,
    existingQuoteLineId?: string
  ) => void
  openEditModal?: (lineItem: any, lineItemIndex: number, lineItemType: string) => void
  isAdjusterEmailModalOpen?: boolean
  setAsViewOnly?: boolean
  isNum?: (str: any) => boolean
  deleteLineItem?: (
    property: QuoteLineItemType,
    index: number,
    reconcile: boolean,
    forceUpdateHandler: () => void
  ) => void
  excludeLineItem?: (
    property: QuoteLineItemType,
    index: number,
    forceUpdateHandler: () => void
  ) => void
  excludeAllLineItems?: (forceUpdateHandler: () => void) => void
  deleteAllLineItems?: (reconcile: boolean, forceUpdateHandler: () => void) => void
  clearReconcileLineItem?: (
    property: QuoteLineItemType,
    index: number,
    forceUpdateHandler: () => void
  ) => void
  getSubtotal?: (property: QuoteLineItemType) => string
  renderLegend?: () => JSX.Element
  setGetQuoteValuesForStaticAction?: (parentfunction?: () => Promise<IQuoteDTO>) => void
  renderClaimButtons?: (section: string) => JSX.Element | JSX.Element[]
  createInvoice?: (quoteInboxItem: QuoteInboxItem, actionId: number) => void
  quoteInboxItem: QuoteInboxItem
  isReferralContactModalOpen?: boolean
  openReferralContactModal?: () => void
  openBundleClaimsModal?: () => void
  setSelectedQuoteUsedItems?: () => void
}

interface IReconcileQuoteState {
  reload: boolean
  generatedLink: string
  expandProfessional: boolean
  expandTechnical: boolean
  expandAnesth: boolean
  expandPhysical: boolean
  expandMisc: boolean
  lineItemDirty: boolean
  pcListStringify: string
  tcListStringify: string
  anListStringify: string
  ptListStringify: string
  miListStringify: string
  isLoading: boolean
  viewOnly: boolean
  hasNotSubmitted: boolean
  buttonsLoading: boolean
}

class ReconcileQuote extends React.Component<IReconcileQuoteProps, IReconcileQuoteState> {
  constructor(props: IReconcileQuoteProps) {
    super(props)
    this.state = {
      reload: false,
      generatedLink: '',
      expandProfessional: true,
      expandTechnical: true,
      expandAnesth: true,
      expandPhysical: true,
      expandMisc: true,
      lineItemDirty: false,
      pcListStringify: '',
      tcListStringify: '',
      anListStringify: '',
      ptListStringify: '',
      miListStringify: '',
      isLoading: true,
      viewOnly: false,
      hasNotSubmitted: true,
      buttonsLoading: true,
    }
    this.forceUpdateHandler = this.forceUpdateHandler.bind(this)
  }

  private wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

  private async waitForQuoteRef(maxAttempts = 10, delayMs = 100) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const hasQuoteRef = !!this.quoteRef.current
      const hasValues = !!this.quoteRef.current?.values

      if (hasQuoteRef && hasValues) {
        return
      }

      await this.wait(delayMs)
    }
  }

  public setMode() {
    var status = this.props.selectedQuote?.status

    if (this.props.setAsViewOnly) {
      this.setState({ viewOnly: true })
    } else {
      if (status == QuoteStatus.Reconciled) {
        this.setState({ viewOnly: true })
      }
    }
  }

  public forceUpdateHandler() {
    var newPC = JSON.stringify(
      this.props.selectedQuote?.professionalComponentLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newTC = JSON.stringify(
      this.props.selectedQuote?.technicalComponentLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newAN = JSON.stringify(
      this.props.selectedQuote?.anesthesiaLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newPT = JSON.stringify(
      this.props.selectedQuote?.physicalTherapyLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newMI = JSON.stringify(
      this.props.selectedQuote?.miscellaneousLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )

    this.setState({
      lineItemDirty:
        this.state.pcListStringify != newPC ||
        this.state.tcListStringify != newTC ||
        this.state.anListStringify != newAN ||
        this.state.ptListStringify != newPT ||
        this.state.miListStringify != newMI,
    })

    this.setMode()
    this.forceUpdate()
  }

  public async componentDidMount() {
    this.props.setGetQuoteValuesForStaticAction!(this.staticActionSave)
    await this.setup()
  }

  public componentDidUpdate() {
    this.props.setGetQuoteValuesForStaticAction!(this.staticActionSave)
  }

  public componentWillUnmount() {
    this.props.setGetQuoteValuesForStaticAction!(undefined)
  }

  public getLineItemStyle = (deleted: boolean) => {
    if (deleted) {
      return 'strike-out-text'
    } else {
      return undefined
    }
  }

  public async setup() {
    const { getProviderTypes, getTypesByCategory, providerTypes } = this.props
    const shouldFetchProviderTypes = !providerTypes || providerTypes.length === 0

    await Promise.all([
      shouldFetchProviderTypes ? getProviderTypes!() : Promise.resolve(),
      getTypesByCategory!('PC'),
      getTypesByCategory!('TC'),
      getTypesByCategory!('MI'),
      this.fetchAnesthesiaLineItems(),
      this.fetchMiscellaneousLineItems(),
      this.fetchPhysicalLineItems(),
      this.fetchProfessionalLineItems(),
      this.fetchTechnicalLineItems(),
      this.fetchAnesthesiaClaimButtons(),
      this.fetchMiscellaneousClaimButtons(),
      this.fetchPhysicalClaimButtons(),
      this.fetchProfessionalClaimButtons(),
      this.fetchTechnicalClaimButtons(),
    ]).then(() => {
      this.setState(
        {
          lineItemDirty: false,
          pcListStringify: JSON.stringify(
            this.props.selectedQuote!.professionalComponentLineItems
          ),
          tcListStringify: JSON.stringify(
            this.props.selectedQuote!.technicalComponentLineItems
          ),
          anListStringify: JSON.stringify(this.props.selectedQuote!.anesthesiaLineItems),
          ptListStringify: JSON.stringify(
            this.props.selectedQuote!.physicalTherapyLineItems
          ),
          miListStringify: JSON.stringify(
            this.props.selectedQuote!.miscellaneousLineItems
          ),
          isLoading: false,
        },
        () => {
          this.props.setSelectedQuoteUsedItems!()
          this.forceUpdateHandler()
        }
      )
    })
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload, isLoading: true }, () => {
      submitForm()
    })
  }

  public staticActionSave = async () => {
    await this.waitForQuoteRef()

    if (!this.quoteRef.current) {
      throw new Error('Quote form reference is not available')
    }

    let values = this.quoteRef.current.values
    if (!values) {
      throw new Error('Quote form values are not available')
    }

    this.quoteRef.current.submitForm()

    const quote = await submitQuoteFormValues(values, true).finally(() => {
      this.setState({ hasNotSubmitted: false }, () => {
        if (this.quoteRef.current) {
          this.quoteRef.current.setSubmitting(false)
        }
      })
    })

    return quote
  }

  public reconcilePTLineItem(index: number) {
    this.props.selectedQuote!.physicalTherapyLineItems![index].reconciledCost =
      this.props.selectedQuote!.physicalTherapyLineItems![index].cost
    this.forceUpdateHandler()
  }

  fetchTechnicalLineItems = async () => {
    return new Promise((resolve): void => {
      const { quoteId } = this.props.match?.params || {}

      if (quoteId) {
        GetTechnicalComponentLineItems(quoteId).then((result) => {
          this.props.selectedQuote!.technicalComponentLineItems! = result
          resolve(result)
        })
      }
    })
  }

  fetchTechnicalClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'TC'

      if (quoteId) {
        GetClaimButtonsWithSummary(quoteId, providerType).then((result) => {
          this.props.selectedQuote!.claimButtonsTC = result.claimButtons
          this.props.selectedQuote!.claimButtonsTCTotals = result.providerTypeSummaries
          this.setState({ buttonsLoading: false })
          resolve(result)
        })
      }
    })
  }

  private mapTechnicalGridDataWithReconciliation = () => {
    if (this.props.selectedQuote?.technicalComponentLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.technicalComponentLineItems!.map(
        (lineItem, index) => (
          <TableRow key={index} className="gridPaddingDense">
            <TableCell style={{ padding: 0 }}>
              {this.props.selectedQuote?.isConsumer &&
              lineItem.movedToQuoteLegacyEOCGroupNumber ? (
                <Tooltip title={`Consumed Line Item`}>
                  <span
                    style={{
                      fontSize: 'large',
                      verticalAlign: 'middle',
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingBottom: '8px',
                      cursor: 'default',
                    }}
                  >
                    🐠&nbsp;&nbsp;
                  </span>
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.reconciledCMS1500LineItemId != undefined
                ? lineItem.reconciledActualLocationGroupName
                : lineItem.providerName}
              {lineItem.reconciledActualLocationGroupName != undefined &&
              lineItem.providerName != lineItem.reconciledActualLocationGroupName ? (
                <Typography
                  variant="caption"
                  style={{ textDecoration: 'line-through red' }}
                >
                  {lineItem.providerName}
                </Typography>
              ) : (
                <></>
              )}
            </TableCell>

            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.typeText ?? ''}
            </TableCell>

            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.reconciledCMS1500LineItemId != undefined
                ? lineItem.reconciledActualCPT
                : lineItem.cptCode}
              {lineItem.reconciledActualCPT != undefined &&
              lineItem.cptCode != lineItem.reconciledActualCPT ? (
                <Typography
                  variant="caption"
                  style={{ textDecoration: 'line-through red' }}
                >
                  {lineItem.cptCode}
                </Typography>
              ) : (
                <></>
              )}
            </TableCell>

            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.costSource != undefined
                ? CostSourceText[lineItem.costSource!]
                : ''}
              {lineItem.otherDescription ? (
                <Tooltip title={lineItem.otherDescription}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>

            <TableCell
              style={{ paddingRight: 0 }}
              align="right"
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {formatCurrency(
                (
                  (lineItem.reconciledActualCost
                    ? lineItem.reconciledActualCost
                    : lineItem.actualCost) ?? 0
                )?.toString()
              )}

              {lineItem.reconciledActualCost != lineItem.actualCost &&
              lineItem.reconciledCMS1500LineItemId != undefined ? (
                <Typography
                  variant="caption"
                  style={{ textDecoration: 'line-through red' }}
                >
                  {lineItem.actualCost != undefined
                    ? formatCurrency(lineItem.actualCost.toString())
                    : ''}
                </Typography>
              ) : (
                <></>
              )}
            </TableCell>

            {lineItem.reconciledActualCost && lineItem.cost == undefined ? (
              <TableCell colSpan={2}></TableCell>
            ) : (
              <>
                <TableCell
                  style={{ paddingRight: 0 }}
                  className={this.getLineItemStyle(
                    lineItem.markedDeleted || lineItem.markExcluded || false
                  )}
                  align="right"
                >
                  {lineItem.cost != undefined
                    ? formatCurrency(lineItem.cost.toString())
                    : ''}
                </TableCell>
                <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
                  {lineItem.calculationExplanation && lineItem.cost != undefined ? (
                    <Tooltip title={lineItem.calculationExplanation}>
                      <InfoOutlined fontSize="small" />
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </TableCell>
              </>
            )}
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {lineItem.reconciledCost != undefined &&
              lineItem.reconciledAppealType === AppealType.OverpaidProvider ? (
                <Typography style={{ fontSize: '0.875rem', color: 'red' }}>
                  ({formatCurrency(lineItem.reconciledCost.toString())})
                </Typography>
              ) : lineItem.reconciledCost != undefined ? (
                formatCurrency(lineItem.reconciledCost.toString())
              ) : (
                ''
              )}
            </TableCell>
            <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
              {lineItem.reconciledCalculationExplanation &&
              lineItem.reconciledCost != undefined ? (
                <Tooltip title={lineItem.reconciledCalculationExplanation}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell align="left">{lineItem.reasonCode}</TableCell>
            <TableCell
              align="left"
              style={{ paddingLeft: 5, paddingRight: 0 }}
            ></TableCell>

            {this.state.viewOnly ? (
              <TableCell></TableCell>
            ) : (
              <>
                <TableCell style={{ width: '46px', paddingRight: 0 }} align="right">
                  {lineItem.cost != undefined ? (
                    lineItem.markExcluded ? (
                      <PlaylistAdd
                        onClick={() =>
                          this.props.excludeLineItem!(
                            QuoteLineItemType.TC,
                            index,
                            this.forceUpdateHandler
                          )
                        }
                        cursor="pointer"
                      ></PlaylistAdd>
                    ) : (
                      <PlaylistRemove
                        onClick={
                          lineItem.reconciledCost != undefined
                            ? () => {}
                            : () =>
                                this.props.excludeLineItem!(
                                  QuoteLineItemType.TC,
                                  index,
                                  this.forceUpdateHandler
                                )
                        }
                        cursor={lineItem.reconciledCost != undefined ? '' : 'pointer'}
                        color={
                          lineItem.reconciledCost != undefined ? 'disabled' : 'error'
                        }
                      />
                    )
                  ) : lineItem.markedDeleted ? (
                    <UndoRounded
                      onClick={() =>
                        this.props.deleteLineItem!(
                          QuoteLineItemType.TC,
                          index,
                          true,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                    />
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Edit
                        onClick={() => this.props.openEditModal!(lineItem, index, 'TC')}
                        cursor="pointer"
                        fontSize="small"
                      />
                      <RemoveCircle
                        onClick={() =>
                          this.props.deleteLineItem!(
                            QuoteLineItemType.TC,
                            index,
                            true,
                            this.forceUpdateHandler
                          )
                        }
                        cursor="pointer"
                        color="error"
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell style={{ width: '58px', paddingLeft: 4 }} align="right">
                  {lineItem.markedDeleted == false &&
                  lineItem.markExcluded != true &&
                  lineItem.cost != undefined ? (
                    lineItem.reconciledCost != undefined ? (
                      <CallSplit
                        color="primary"
                        cursor="pointer"
                        onClick={() =>
                          this.props.clearReconcileLineItem!(
                            QuoteLineItemType.TC,
                            index,
                            this.forceUpdateHandler
                          )
                        }
                      />
                    ) : (
                      <CallMerge
                        color="primary"
                        cursor="pointer"
                        onClick={() =>
                          this.props.openReconModal!(
                            this.props.selectedQuote!,
                            'TC',
                            lineItem.id
                          )
                        }
                      />
                    )
                  ) : (
                    <></>
                  )}
                </TableCell>
              </>
            )}
          </TableRow>
        )
      )
    }
  }

  fetchProfessionalLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      if (quoteId) {
        GetProfessionalComponentLineItems(quoteId).then((result) => {
          this.props.selectedQuote!.professionalComponentLineItems! = result
          resolve(result)
        })
      }
    })
  }

  fetchProfessionalClaimButtons = async () => {
    return new Promise((resolve): void => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'PC'

      if (quoteId) {
        GetClaimButtonsWithSummary(quoteId, providerType).then((result) => {
          this.props.selectedQuote!.claimButtonsPC = result.claimButtons
          this.props.selectedQuote!.claimButtonsPCTotals = result.providerTypeSummaries
          this.setState({ buttonsLoading: false })
          resolve(result)
        })
      }
    })
  }

  private mapProfessionalGridDataWithReconciliation = () => {
    if (this.props.selectedQuote?.professionalComponentLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.professionalComponentLineItems!.map(
        (lineItem, index) => (
          <TableRow key={index} className="gridPaddingDense">
            <TableCell>
              {this.props.selectedQuote?.isConsumer &&
              lineItem.movedToQuoteLegacyEOCGroupNumber ? (
                <Tooltip title={`Consumed Line Item`}>
                  <span
                    style={{
                      fontSize: 'large',
                      verticalAlign: 'middle',
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingBottom: '8px',
                      cursor: 'default',
                    }}
                  >
                    🐠&nbsp;&nbsp;
                  </span>
                </Tooltip>
              ) : lineItem.originalQuoteId &&
                lineItem.originalQuoteId == this.props.selectedQuote!.id ? (
                <Tooltip title="This line item has been consumed by another quote.">
                  <span style={{ fontSize: 'large', verticalAlign: 'middle' }}>🐠</span>
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.reconciledCMS1500LineItemId != undefined
                ? lineItem.reconciledActualLocationGroupName
                : lineItem.providerName}
              {lineItem.reconciledActualLocationGroupName != undefined &&
              lineItem.providerName != lineItem.reconciledActualLocationGroupName ? (
                <Typography
                  variant="caption"
                  style={{ textDecoration: 'line-through red' }}
                >
                  {lineItem.providerName}
                </Typography>
              ) : (
                <></>
              )}
            </TableCell>

            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.physicianName}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.typeText ?? ''}
            </TableCell>

            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.reconciledCMS1500LineItemId != undefined
                ? lineItem.reconciledActualCPT
                : lineItem.cptCode}
              {lineItem.reconciledActualCPT != undefined &&
              lineItem.cptCode != lineItem.reconciledActualCPT ? (
                <Typography
                  variant="caption"
                  style={{ textDecoration: 'line-through red' }}
                >
                  {lineItem.cptCode}
                </Typography>
              ) : (
                <></>
              )}
            </TableCell>

            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.costSource != undefined
                ? CostSourceText[lineItem.costSource!]
                : ''}
              {lineItem.otherDescription ? (
                <Tooltip title={lineItem.otherDescription}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>

            <TableCell
              align="right"
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {(lineItem.cost == 0 || lineItem.cost == undefined) &&
              lineItem.assistantMultiplier != undefined &&
              lineItem.reconciledCost == undefined
                ? 'N/A'
                : formatCurrency(
                    (
                      (lineItem.reconciledActualCost
                        ? lineItem.reconciledActualCost
                        : lineItem.actualCost) ?? 0
                    )?.toString()
                  )}

              {lineItem.reconciledActualCost != lineItem.actualCost &&
              lineItem.reconciledCMS1500LineItemId != undefined ? (
                <Typography
                  variant="caption"
                  style={{ textDecoration: 'line-through red' }}
                >
                  {lineItem.actualCost != undefined
                    ? formatCurrency(lineItem.actualCost.toString())
                    : ''}
                </Typography>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell align="left" style={{ paddingLeft: 5 }}>
              {lineItem.assistantSurgeonInfo ? (
                <Tooltip title={lineItem.assistantSurgeonInfo}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>

            {lineItem.reconciledActualCost && lineItem.cost == undefined ? (
              <TableCell colSpan={2}></TableCell>
            ) : (
              <>
                <TableCell
                  style={{ paddingRight: 0 }}
                  className={this.getLineItemStyle(
                    lineItem.markedDeleted || lineItem.markExcluded || false
                  )}
                  align="right"
                >
                  {lineItem.cost == 0 &&
                  lineItem.assistantMultiplier != undefined &&
                  lineItem.calculationExplanation == undefined &&
                  !lineItem.markedDeleted
                    ? 'Will Calculate on Save'
                    : lineItem.cost != undefined
                      ? formatCurrency(lineItem.cost.toString())
                      : ''}
                </TableCell>
                <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
                  {lineItem.calculationExplanation && lineItem.cost != undefined ? (
                    <Tooltip title={lineItem.calculationExplanation}>
                      <InfoOutlined fontSize="small" />
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </TableCell>
              </>
            )}
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {(lineItem.cost == 0 || lineItem.cost == undefined) &&
              lineItem.assistantMultiplier != undefined &&
              lineItem.reconciledCost == undefined &&
              !lineItem.markedDeleted ? (
                'Will Calculate on Save'
              ) : lineItem.reconciledCost != undefined &&
                lineItem.reconciledAppealType === AppealType.OverpaidProvider ? (
                <Typography style={{ fontSize: '0.875rem', color: 'red' }}>
                  ({formatCurrency(lineItem.reconciledCost.toString())})
                </Typography>
              ) : lineItem.reconciledCost != undefined ? (
                formatCurrency(lineItem.reconciledCost.toString())
              ) : (
                ''
              )}
            </TableCell>
            <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
              {lineItem.reconciledCalculationExplanation &&
              lineItem.reconciledCost != undefined ? (
                <Tooltip title={lineItem.reconciledCalculationExplanation}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell align="left">{lineItem.reasonCode}</TableCell>
            <TableCell
              align="left"
              style={{ paddingLeft: 5, paddingRight: 0 }}
            ></TableCell>
            {this.state.viewOnly ||
            lineItem.originalQuoteId == this.props.selectedQuote!.id ? (
              <TableCell></TableCell>
            ) : (
              <>
                <TableCell style={{ width: '46px', paddingRight: 0 }} align="right">
                  {lineItem.cost != undefined ? (
                    lineItem.markExcluded ? (
                      <PlaylistAdd
                        onClick={() =>
                          this.props.excludeLineItem!(
                            QuoteLineItemType.PC,
                            index,
                            this.forceUpdateHandler
                          )
                        }
                        cursor="pointer"
                      ></PlaylistAdd>
                    ) : (
                      <PlaylistRemove
                        onClick={
                          lineItem.reconciledCost != undefined
                            ? () => {}
                            : () =>
                                this.props.excludeLineItem!(
                                  QuoteLineItemType.PC,
                                  index,
                                  this.forceUpdateHandler
                                )
                        }
                        cursor={lineItem.reconciledCost != undefined ? '' : 'pointer'}
                        color={
                          lineItem.reconciledCost != undefined ? 'disabled' : 'error'
                        }
                      />
                    )
                  ) : lineItem.markedDeleted ? (
                    <UndoRounded
                      onClick={() =>
                        this.props.deleteLineItem!(
                          QuoteLineItemType.PC,
                          index,
                          true,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                    />
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Edit
                        onClick={() => this.props.openEditModal!(lineItem, index, 'PC')}
                        cursor="pointer"
                        fontSize="small"
                      />
                      <RemoveCircle
                        onClick={() =>
                          this.props.deleteLineItem!(
                            QuoteLineItemType.PC,
                            index,
                            true,
                            this.forceUpdateHandler
                          )
                        }
                        cursor="pointer"
                        color="error"
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell style={{ width: '58px', paddingLeft: 4 }} align="right">
                  {lineItem.markExcluded != true && lineItem.cost != undefined ? (
                    lineItem.reconciledCost != undefined ? (
                      <CallSplit
                        color="primary"
                        cursor="pointer"
                        onClick={() =>
                          this.props.clearReconcileLineItem!(
                            QuoteLineItemType.PC,
                            index,
                            this.forceUpdateHandler
                          )
                        }
                      />
                    ) : (
                      <CallMerge
                        color="primary"
                        cursor="pointer"
                        onClick={() =>
                          this.props.openReconModal!(
                            this.props.selectedQuote!,
                            'PC',
                            lineItem.id
                          )
                        }
                      />
                    )
                  ) : (
                    <></>
                  )}
                </TableCell>
              </>
            )}
          </TableRow>
        )
      )
    }
  }

  fetchAnesthesiaLineItems = async () => {
    return new Promise((resolve): void => {
      const { quoteId } = this.props.match?.params || {}

      if (quoteId) {
        GetAnesthesiaLineItems(quoteId).then((result) => {
          this.props.selectedQuote!.anesthesiaLineItems! = result
          resolve(result)
        })
      }
    })
  }

  fetchAnesthesiaClaimButtons = async () => {
    return new Promise((resolve): void => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'AN'

      if (quoteId) {
        GetClaimButtonsWithSummary(quoteId, providerType).then((result) => {
          this.props.selectedQuote!.claimButtonsAN = result.claimButtons
          this.props.selectedQuote!.claimButtonsANTotals = result.providerTypeSummaries
          this.setState({ buttonsLoading: false })
          resolve(result)
        })
      }
    })
  }

  private mapAnesthesiaGridDataReconciliation = () => {
    if (this.props.selectedQuote?.anesthesiaLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.anesthesiaLineItems!.map((lineItem, index) => (
        <TableRow key={index} className="gridPaddingDense">
          <TableCell style={{ padding: 0 }}>
            {this.props.selectedQuote?.isConsumer &&
            lineItem.movedToQuoteLegacyEOCGroupNumber ? (
              <Tooltip title={`Consumed Line Item`}>
                <span
                  style={{
                    fontSize: 'large',
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    alignItems: 'center',
                    paddingBottom: '8px',
                    cursor: 'default',
                  }}
                >
                  🐠&nbsp;&nbsp;
                </span>
              </Tooltip>
            ) : lineItem.reconciledCMS1500LineItemId == undefined &&
              lineItem.reconciledCost != undefined ? (
              <Tooltip title="Reconciled with No Claim">
                <MoneyOffOutlined />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.reconciledCMS1500LineItemId != undefined
              ? lineItem.reconciledActualLocationGroupName
              : lineItem.providerName}
            {lineItem.reconciledActualLocationGroupName != undefined &&
            lineItem.providerName != lineItem.reconciledActualLocationGroupName ? (
              <Typography
                variant="caption"
                style={{ textDecoration: 'line-through red' }}
              >
                {lineItem.providerName}
              </Typography>
            ) : (
              <></>
            )}
          </TableCell>

          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.physicianName ?? ''}
          </TableCell>

          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.reconciledCMS1500LineItemId != undefined
              ? lineItem.reconciledActualCPT
              : lineItem.cptCode}
            {lineItem.reconciledCMS1500LineItemId != undefined &&
            lineItem.cptCode != lineItem.reconciledActualCPT ? (
              <Typography
                variant="caption"
                style={{ textDecoration: 'line-through red' }}
              >
                {lineItem.cptCode}
              </Typography>
            ) : (
              <></>
            )}
          </TableCell>

          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.costSource != undefined ? CostSourceText[lineItem.costSource!] : ''}
            {lineItem.otherDescription ? (
              <Tooltip title={lineItem.otherDescription}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>

          <TableCell
            style={{ paddingRight: 0 }}
            align="right"
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {formatCurrency(
              (
                (lineItem.reconciledActualCost
                  ? lineItem.reconciledActualCost
                  : lineItem.actualCost) ?? 0
              )?.toString()
            )}
            {lineItem.reconciledActualCost != lineItem.actualCost &&
            lineItem.reconciledCMS1500LineItemId != undefined ? (
              <Typography
                variant="caption"
                style={{ textDecoration: 'line-through red' }}
              >
                {lineItem.actualCost != undefined
                  ? formatCurrency(lineItem.actualCost.toString())
                  : ''}
              </Typography>
            ) : (
              <></>
            )}
          </TableCell>

          {lineItem.reconciledActualCost && lineItem.cost == undefined ? (
            <TableCell colSpan={2}></TableCell>
          ) : (
            <>
              <TableCell
                style={{ paddingRight: 0 }}
                className={this.getLineItemStyle(
                  lineItem.markedDeleted || lineItem.markExcluded || false
                )}
                align="right"
              >
                {lineItem.cost != undefined
                  ? formatCurrency(lineItem.cost.toString())
                  : ''}
              </TableCell>
              <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
                {lineItem.calculationExplanation && lineItem.cost != undefined ? (
                  <Tooltip title={lineItem.calculationExplanation}>
                    <InfoOutlined fontSize="small" />
                  </Tooltip>
                ) : (
                  <></>
                )}
              </TableCell>
            </>
          )}
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
            align="right"
          >
            {lineItem.reconciledCost != undefined &&
            lineItem.reconciledAppealType === AppealType.OverpaidProvider ? (
              <Typography style={{ fontSize: '0.875rem', color: 'red' }}>
                ({formatCurrency(lineItem.reconciledCost.toString())})
              </Typography>
            ) : lineItem.reconciledCost != undefined ? (
              formatCurrency(lineItem.reconciledCost.toString())
            ) : (
              ''
            )}
          </TableCell>
          <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
            {lineItem.reconciledCalculationExplanation &&
            lineItem.reconciledCost != undefined ? (
              <Tooltip title={lineItem.reconciledCalculationExplanation}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell align="left">{lineItem.reasonCode}</TableCell>
          <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}></TableCell>
          {this.state.viewOnly ? (
            <TableCell></TableCell>
          ) : (
            <>
              <TableCell style={{ width: '46px', paddingRight: 0 }} align="right">
                {lineItem.cost != undefined ? (
                  lineItem.markExcluded ? (
                    <PlaylistAdd
                      onClick={() =>
                        this.props.excludeLineItem!(
                          QuoteLineItemType.AN,
                          index,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                    ></PlaylistAdd>
                  ) : (
                    <PlaylistRemove
                      onClick={
                        lineItem.reconciledCost != undefined
                          ? () => {}
                          : () =>
                              this.props.excludeLineItem!(
                                QuoteLineItemType.AN,
                                index,
                                this.forceUpdateHandler
                              )
                      }
                      cursor={lineItem.reconciledCost != undefined ? '' : 'pointer'}
                      color={lineItem.reconciledCost != undefined ? 'disabled' : 'error'}
                    />
                  )
                ) : lineItem.markedDeleted ? (
                  <UndoRounded
                    onClick={() =>
                      this.props.deleteLineItem!(
                        QuoteLineItemType.AN,
                        index,
                        true,
                        this.forceUpdateHandler
                      )
                    }
                    cursor="pointer"
                  />
                ) : (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Edit
                      onClick={() => this.props.openEditModal!(lineItem, index, 'AN')}
                      cursor="pointer"
                      fontSize="small"
                    />
                    <RemoveCircle
                      onClick={() =>
                        this.props.deleteLineItem!(
                          QuoteLineItemType.AN,
                          index,
                          true,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                      color="error"
                    />
                  </Box>
                )}
              </TableCell>
              <TableCell style={{ width: '58px', paddingLeft: 4 }} align="right">
                {lineItem.markExcluded != true && lineItem.cost != undefined ? (
                  lineItem.reconciledCost != undefined ? (
                    <CallSplit
                      color="primary"
                      cursor="pointer"
                      onClick={() =>
                        this.props.clearReconcileLineItem!(
                          QuoteLineItemType.AN,
                          index,
                          this.forceUpdateHandler
                        )
                      }
                    />
                  ) : (
                    <CallMerge
                      color="primary"
                      cursor="pointer"
                      onClick={() =>
                        this.props.openReconModal!(
                          this.props.selectedQuote!,
                          'AN',
                          lineItem.id
                        )
                      }
                    />
                  )
                ) : (
                  <></>
                )}
              </TableCell>
            </>
          )}
        </TableRow>
      ))
    }
  }

  fetchPhysicalClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'PT'

      if (quoteId) {
        GetClaimButtonsWithSummary(quoteId, providerType).then((result) => {
          this.props.selectedQuote!.claimButtonsPT = result.claimButtons
          this.props.selectedQuote!.claimButtonsPTTotals = result.providerTypeSummaries
          this.setState({ buttonsLoading: false })
          resolve(result)
        })
      }
    })
  }

  fetchPhysicalLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      if (quoteId) {
        GetPhysicalTherapyLineItems(quoteId).then((result) => {
          this.props.selectedQuote!.physicalTherapyLineItems! = result
          resolve(result)
        })
      }
    })
  }

  private mapPhysicalGridDataReconciliation = () => {
    if (this.props.selectedQuote?.physicalTherapyLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.physicalTherapyLineItems!.map(
        (lineItem, index) => (
          <TableRow key={index} className="gridPadding">
            <TableCell style={{ padding: 0 }}>
              {this.props.selectedQuote?.isConsumer &&
              lineItem.movedToQuoteLegacyEOCGroupNumber ? (
                <Tooltip title={`Consumed Line Item`}>
                  <span
                    style={{
                      fontSize: 'large',
                      verticalAlign: 'middle',
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingBottom: '8px',
                      cursor: 'default',
                    }}
                  >
                    🐠&nbsp;&nbsp;
                  </span>
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              style={{ whiteSpace: 'nowrap' }}
            >
              {lineItem.providerName}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
            >
              {lineItem.numberOfVisits?.toString()}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {lineItem.actualCostPerVisit
                ? formatCurrency(lineItem.actualCostPerVisit?.toString())
                : ''}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {lineItem.actualCostPerVisit && lineItem.numberOfVisits
                ? formatCurrency(
                    (lineItem.actualCostPerVisit * lineItem.numberOfVisits)?.toString()
                  )
                : ''}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {lineItem.costPerVisit
                ? formatCurrency(lineItem.costPerVisit?.toString())
                : ''}
            </TableCell>
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {lineItem.cost != undefined ? formatCurrency(lineItem.cost.toString()) : ''}
            </TableCell>
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(
                lineItem.markedDeleted || lineItem.markExcluded || false
              )}
              align="right"
            >
              {lineItem.reconciledCost != undefined
                ? formatCurrency(lineItem.reconciledCost.toString())
                : ''}
            </TableCell>
            {this.state.viewOnly ? (
              <TableCell></TableCell>
            ) : (
              <>
                <TableCell align="right">
                  {lineItem.cost != undefined ? (
                    lineItem.markExcluded ? (
                      <PlaylistAdd
                        onClick={() =>
                          this.props.excludeLineItem!(
                            QuoteLineItemType.PT,
                            index,
                            this.forceUpdateHandler
                          )
                        }
                        cursor="pointer"
                      ></PlaylistAdd>
                    ) : (
                      <PlaylistRemove
                        onClick={
                          lineItem.reconciledCost != undefined
                            ? () => {}
                            : () =>
                                this.props.excludeLineItem!(
                                  QuoteLineItemType.PT,
                                  index,
                                  this.forceUpdateHandler
                                )
                        }
                        cursor={lineItem.reconciledCost != undefined ? '' : 'pointer'}
                        color={
                          lineItem.reconciledCost != undefined ? 'disabled' : 'error'
                        }
                      />
                    )
                  ) : lineItem.markedDeleted ? (
                    <UndoRounded
                      onClick={() =>
                        this.props.deleteLineItem!(
                          QuoteLineItemType.PT,
                          index,
                          true,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                    />
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Edit
                        onClick={() => this.props.openEditModal!(lineItem, index, 'PT')}
                        cursor="pointer"
                        fontSize="small"
                      />
                      <RemoveCircle
                        onClick={() =>
                          this.props.deleteLineItem!(
                            QuoteLineItemType.PT,
                            index,
                            true,
                            this.forceUpdateHandler
                          )
                        }
                        cursor="pointer"
                        color="error"
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  {lineItem.markedDeleted == false &&
                  lineItem.markExcluded != true &&
                  lineItem.cost != undefined ? (
                    lineItem.reconciledCost != undefined ? (
                      <CallSplit
                        color="primary"
                        cursor="pointer"
                        onClick={() =>
                          this.props.clearReconcileLineItem!(
                            QuoteLineItemType.PT,
                            index,
                            this.forceUpdateHandler
                          )
                        }
                      />
                    ) : (
                      <CallMerge
                        color="primary"
                        cursor="pointer"
                        onClick={() => this.reconcilePTLineItem(index)}
                      />
                    )
                  ) : (
                    <></>
                  )}
                </TableCell>
              </>
            )}
          </TableRow>
        )
      )
    }
  }

  fetchMiscellaneousLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      if (quoteId) {
        GetMiscellaneousLineItems(quoteId).then((result) => {
          this.props.selectedQuote!.miscellaneousLineItems! = result
          resolve(result)
        })
      }
    })
  }

  fetchMiscellaneousClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'MI'

      if (quoteId) {
        GetClaimButtonsWithSummary(quoteId, providerType).then((result) => {
          this.props.selectedQuote!.claimButtonsMI = result.claimButtons
          this.props.selectedQuote!.claimButtonsMITotals = result.providerTypeSummaries
          this.setState({ buttonsLoading: false })
          resolve(result)
        })
      }
    })
  }

  private mapMiscGridDataWithReconciliation = () => {
    if (this.props.selectedQuote?.miscellaneousLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.miscellaneousLineItems!.map((lineItem, index) => (
        <TableRow key={index} className="gridPaddingDense">
          <TableCell style={{ padding: 0 }}>
            {this.props.selectedQuote?.isConsumer &&
            lineItem.movedToQuoteLegacyEOCGroupNumber ? (
              <Tooltip title={`Consumed Line Item`}>
                <span
                  style={{
                    fontSize: 'large',
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    alignItems: 'center',
                    paddingBottom: '8px',
                    cursor: 'default',
                  }}
                >
                  🐠&nbsp;&nbsp;
                </span>
              </Tooltip>
            ) : lineItem.reconciledCMS1500LineItemId == undefined &&
              lineItem.reconciledCost != undefined ? (
              <Tooltip title="Reconciled with No Claim">
                <MoneyOffOutlined />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.typeText ?? ''}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.reconciledCMS1500LineItemId != undefined
              ? lineItem.reconciledActualLocationGroupName
              : lineItem.providerName}
            {lineItem.reconciledActualLocationGroupName != undefined &&
            lineItem.providerName != lineItem.reconciledActualLocationGroupName ? (
              <Typography
                variant="caption"
                style={{ textDecoration: 'line-through red' }}
              >
                {lineItem.providerName}
              </Typography>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.lineDescription ?? ''}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.reconciledCMS1500LineItemId != undefined
              ? lineItem.reconciledActualCPT
              : lineItem.cptCode}
            {lineItem.reconciledActualCPT != undefined &&
            lineItem.cptCode != lineItem.reconciledActualCPT ? (
              <Typography
                variant="caption"
                style={{ textDecoration: 'line-through red' }}
              >
                {lineItem.cptCode}
              </Typography>
            ) : (
              <></>
            )}
          </TableCell>

          <TableCell
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {lineItem.costSource != undefined ? CostSourceText[lineItem.costSource!] : ''}
            {lineItem.otherDescription ? (
              <Tooltip title={lineItem.otherDescription}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>

          <TableCell
            style={{ paddingRight: 0 }}
            align="right"
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
          >
            {formatCurrency(
              (
                (lineItem.reconciledActualCost
                  ? lineItem.reconciledActualCost
                  : lineItem.actualCost) ?? 0
              )?.toString()
            )}
            {lineItem.reconciledActualCost != lineItem.actualCost &&
            lineItem.reconciledCMS1500LineItemId != undefined ? (
              <Typography
                variant="caption"
                style={{ textDecoration: 'line-through red' }}
              >
                {lineItem.actualCost != undefined
                  ? formatCurrency(lineItem.actualCost.toString())
                  : ''}
              </Typography>
            ) : (
              <></>
            )}
          </TableCell>

          {lineItem.reconciledActualCost && lineItem.cost == undefined ? (
            <TableCell colSpan={2}></TableCell>
          ) : (
            <>
              <TableCell
                style={{ paddingRight: 0 }}
                className={this.getLineItemStyle(
                  lineItem.markedDeleted || lineItem.markExcluded || false
                )}
                align="right"
              >
                {lineItem.cost != undefined
                  ? formatCurrency(lineItem.cost.toString())
                  : ''}
              </TableCell>
              <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
                {lineItem.calculationExplanation && lineItem.cost != undefined ? (
                  <Tooltip title={lineItem.calculationExplanation}>
                    <InfoOutlined fontSize="small" />
                  </Tooltip>
                ) : (
                  <></>
                )}
              </TableCell>
            </>
          )}
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(
              lineItem.markedDeleted || lineItem.markExcluded || false
            )}
            align="right"
          >
            {lineItem.reconciledCost != undefined &&
            lineItem.reconciledAppealType === AppealType.OverpaidProvider ? (
              <Typography style={{ fontSize: '0.875rem', color: 'red' }}>
                ({formatCurrency(lineItem.reconciledCost.toString())})
              </Typography>
            ) : lineItem.reconciledCost != undefined ? (
              formatCurrency(lineItem.reconciledCost.toString())
            ) : (
              ''
            )}
          </TableCell>
          <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}>
            {lineItem.reconciledCalculationExplanation &&
            lineItem.reconciledCost != undefined ? (
              <Tooltip title={lineItem.reconciledCalculationExplanation}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell align="left">{lineItem.reasonCode}</TableCell>
          <TableCell align="left" style={{ paddingLeft: 5, paddingRight: 0 }}></TableCell>
          {this.state.viewOnly ? (
            <TableCell></TableCell>
          ) : (
            <>
              <TableCell style={{ width: '46px', paddingRight: 0 }} align="right">
                {lineItem.cost != undefined ? (
                  lineItem.markExcluded ? (
                    <PlaylistAdd
                      onClick={() =>
                        this.props.excludeLineItem!(
                          QuoteLineItemType.MI,
                          index,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                    ></PlaylistAdd>
                  ) : (
                    <PlaylistRemove
                      onClick={
                        lineItem.reconciledCost != undefined
                          ? () => {}
                          : () =>
                              this.props.excludeLineItem!(
                                QuoteLineItemType.MI,
                                index,
                                this.forceUpdateHandler
                              )
                      }
                      cursor={lineItem.reconciledCost != undefined ? '' : 'pointer'}
                      color={lineItem.reconciledCost != undefined ? 'disabled' : 'error'}
                    />
                  )
                ) : lineItem.markedDeleted ? (
                  <UndoRounded
                    onClick={() =>
                      this.props.deleteLineItem!(
                        QuoteLineItemType.MI,
                        index,
                        true,
                        this.forceUpdateHandler
                      )
                    }
                    cursor="pointer"
                  />
                ) : (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Edit
                      onClick={() => this.props.openEditModal!(lineItem, index, 'MI')}
                      cursor="pointer"
                      fontSize="small"
                    />
                    <RemoveCircle
                      onClick={() =>
                        this.props.deleteLineItem!(
                          QuoteLineItemType.MI,
                          index,
                          true,
                          this.forceUpdateHandler
                        )
                      }
                      cursor="pointer"
                      color="error"
                    />
                  </Box>
                )}
              </TableCell>
              <TableCell style={{ width: '58px', paddingLeft: 4 }} align="right">
                {lineItem.markExcluded != true && lineItem.cost != undefined ? (
                  lineItem.reconciledCost != undefined ? (
                    <CallSplit
                      color="primary"
                      cursor="pointer"
                      onClick={() =>
                        this.props.clearReconcileLineItem!(
                          QuoteLineItemType.MI,
                          index,
                          this.forceUpdateHandler
                        )
                      }
                    />
                  ) : (
                    <CallMerge
                      color="primary"
                      cursor="pointer"
                      onClick={() =>
                        this.props.openReconModal!(
                          this.props.selectedQuote!,
                          'MI',
                          lineItem.id
                        )
                      }
                    />
                  )
                ) : (
                  <></>
                )}
              </TableCell>
            </>
          )}
        </TableRow>
      ))
    }
  }

  private renderPC() {
    return (
      <Accordion
        expanded={this.state.expandProfessional}
        onChange={() =>
          this.setState({ expandProfessional: !this.state.expandProfessional })
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsPCTotals &&
              this.props.selectedQuote.claimButtonsPCTotals.length > 0 &&
              this.props.selectedQuote.claimButtonsPCTotals[0].claimCount > 0
                ? `PROFESSIONAL COMPONENTS — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsPCTotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsPCTotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsPCTotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'PROFESSIONAL COMPONENTS'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Quoted: ' +
                  this.props.getSubtotal!(QuoteLineItemType.PC) +
                  ' (' +
                  this.props.selectedQuote?.professionalComponentLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length +
                  (this.props.selectedQuote?.professionalComponentLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
              <span>
                {' Reconciled: ' +
                  this.getReconSubtotal(QuoteLineItemType.PC) +
                  ' (' +
                  this.props.selectedQuote?.professionalComponentLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length +
                  (this.props.selectedQuote?.professionalComponentLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('PC')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Physician</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                        paddingLeft: 0,
                      }}
                    >
                      Billed Charges
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={2}
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Reconciled $
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell align="left">Reason Code</TableCell>
                    <TableCell align="right" style={{ paddingRight: '14px' }} colSpan={3}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openReconModal!(
                              this.props.selectedQuote!,
                              'PC',
                              undefined
                            )
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapProfessionalGridDataWithReconciliation()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderTC() {
    return (
      <Accordion
        expanded={this.state.expandTechnical}
        onChange={() => this.setState({ expandTechnical: !this.state.expandTechnical })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsTCTotals &&
              this.props.selectedQuote.claimButtonsTCTotals.length > 0 &&
              this.props.selectedQuote.claimButtonsTCTotals[0].claimCount > 0
                ? `TECHNICAL COMPONENTS — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsTCTotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsTCTotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsTCTotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'TECHNICAL COMPONENTS'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Quoted: ' +
                  this.props.getSubtotal!(QuoteLineItemType.TC) +
                  ' (' +
                  this.props.selectedQuote?.technicalComponentLineItems!.length +
                  (this.props.selectedQuote?.technicalComponentLineItems!.length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
              <span>
                {' Reconciled: ' +
                  this.getReconSubtotal(QuoteLineItemType.TC) +
                  ' (' +
                  this.props.selectedQuote?.technicalComponentLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length +
                  (this.props.selectedQuote?.technicalComponentLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('TC')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                        paddingLeft: 0,
                      }}
                    >
                      Billed Charges
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={2}
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Reconciled $
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell align="left">Reason Code</TableCell>
                    <TableCell align="right" style={{ paddingRight: '14px' }} colSpan={3}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openReconModal!(
                              this.props.selectedQuote!,
                              'TC',
                              undefined
                            )
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapTechnicalGridDataWithReconciliation()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderAN() {
    return (
      <Accordion
        expanded={this.state.expandAnesth}
        onChange={() => this.setState({ expandAnesth: !this.state.expandAnesth })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsANTotals &&
              this.props.selectedQuote.claimButtonsANTotals.length > 0 &&
              this.props.selectedQuote.claimButtonsANTotals[0].claimCount > 0
                ? `ANESTHESIOLOGIST — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsANTotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsANTotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsANTotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'ANESTHESIOLOGIST'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Quoted: ' +
                  this.props.getSubtotal!(QuoteLineItemType.AN) +
                  ' (' +
                  this.props.selectedQuote?.anesthesiaLineItems!.length +
                  (this.props.selectedQuote?.anesthesiaLineItems!.length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
              <span>
                {' Reconciled: ' +
                  this.getReconSubtotal(QuoteLineItemType.AN) +
                  ' (' +
                  this.props.selectedQuote?.anesthesiaLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length +
                  (this.props.selectedQuote?.anesthesiaLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('AN')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table" sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell style={{ padding: 0 }} />
                    <TableCell>Provider</TableCell>
                    <TableCell>Physician</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell align="right" style={{ paddingRight: 0 }}>
                      Billed Charges
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={2}
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Reconciled $
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell align="left">Reason Code</TableCell>
                    <TableCell align="right" style={{ paddingRight: '14px' }} colSpan={3}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openReconModal!(
                              this.props.selectedQuote!,
                              'AN',
                              undefined
                            )
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapAnesthesiaGridDataReconciliation()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderPT() {
    return (
      <Accordion
        expanded={this.state.expandPhysical}
        onChange={() => this.setState({ expandPhysical: !this.state.expandPhysical })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <span>
              <Typography display="inline" color="textSecondary" variant="body2">
                {this.props.selectedQuote?.claimButtonsPTTotals &&
                this.props.selectedQuote.claimButtonsPTTotals.length > 0 &&
                this.props.selectedQuote.claimButtonsPTTotals[0].claimCount > 0
                  ? `PHYSICAL THERAPY — Claim Charges: ${formatCurrency(
                      this.props.selectedQuote.claimButtonsPTTotals[0].totalClaimChargeAmount?.toString() ??
                        ''
                    )} (${this.props.selectedQuote.claimButtonsPTTotals[0].claimCount} ${
                      this.props.selectedQuote.claimButtonsPTTotals[0].claimCount === 1
                        ? 'item'
                        : 'items'
                    })`
                  : 'PHYSICAL THERAPY'}
              </Typography>
              {this.props.selectedQuote?.isNoPtToBeScheduled ? (
                <Typography
                  color="primary"
                  variant="caption"
                  style={{ paddingLeft: '8px' }}
                >
                  <strong>No PT to be scheduled for this patient</strong>
                </Typography>
              ) : (
                ''
              )}
            </span>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Quoted: ' +
                  this.props.getSubtotal!(QuoteLineItemType.PT) +
                  ' (' +
                  this.props.selectedQuote?.physicalTherapyLineItems!.length +
                  (this.props.selectedQuote?.physicalTherapyLineItems!.length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
              <span>
                {' Reconciled: ' +
                  this.getReconSubtotal(QuoteLineItemType.PT) +
                  ' (' +
                  this.props.selectedQuote?.physicalTherapyLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length +
                  (this.props.selectedQuote?.physicalTherapyLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('PT')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPadding">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Number of Visits</TableCell>
                    <TableCell align="right">Est. U&C Per Visit</TableCell>
                    <TableCell align="right">Est. U&C $</TableCell>
                    <TableCell align="right">Quoted Per Visit</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Reconciled $
                    </TableCell>
                    <TableCell align="right" style={{ paddingRight: '14px' }} colSpan={3}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openReconModal!(
                              this.props.selectedQuote!,
                              'PT',
                              undefined
                            )
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapPhysicalGridDataReconciliation()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderMI() {
    return (
      <Accordion
        expanded={this.state.expandMisc}
        onChange={() => this.setState({ expandMisc: !this.state.expandMisc })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsMITotals &&
              this.props.selectedQuote.claimButtonsMITotals.length > 0 &&
              this.props.selectedQuote.claimButtonsMITotals[0].claimCount > 0
                ? `MISCELLANEOUS — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsMITotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsMITotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsMITotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'MISCELLANEOUS'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Quoted: ' +
                  this.props.getSubtotal!(QuoteLineItemType.MI) +
                  ' (' +
                  this.props.selectedQuote?.miscellaneousLineItems!.length +
                  (this.props.selectedQuote?.miscellaneousLineItems!.length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
              <span>
                {' Reconciled: ' +
                  this.getReconSubtotal(QuoteLineItemType.MI) +
                  ' (' +
                  this.props.selectedQuote?.miscellaneousLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length +
                  (this.props.selectedQuote?.miscellaneousLineItems!.filter(
                    (x) => x.reconciledCost
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('MI')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table" sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell style={{ padding: 0 }} />
                    <TableCell>Type</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell align="right" style={{ paddingRight: 0 }}>
                      Billed Charges
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell
                      align="right"
                      colSpan={2}
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Reconciled $
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell align="left">Reason Code</TableCell>
                    <TableCell align="right" style={{ paddingRight: '14px' }} colSpan={3}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openReconModal!(
                              this.props.selectedQuote!,
                              'MI',
                              undefined
                            )
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapMiscGridDataWithReconciliation()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private getReconSubtotal = (property: QuoteLineItemType) => {
    var subtotal = 0

    if (this.props.selectedQuote) {
      switch (property) {
        case QuoteLineItemType.PC:
          this.props.selectedQuote
            .professionalComponentLineItems!.filter((x) => !x.markExcluded)
            .forEach((x) => {
              if (x.reconciledCost) {
                subtotal +=
                  x.reconciledAppealType !== AppealType.OverpaidProvider
                    ? +x.reconciledCost
                    : -x.reconciledCost
              }
            })
          break
        case QuoteLineItemType.TC:
          this.props.selectedQuote
            .technicalComponentLineItems!.filter((x) => !x.markExcluded)
            .forEach((x) => {
              if (x.reconciledCost) {
                subtotal +=
                  x.reconciledAppealType !== AppealType.OverpaidProvider
                    ? +x.reconciledCost
                    : -x.reconciledCost
              }
            })
          break
        case QuoteLineItemType.AN:
          this.props.selectedQuote
            .anesthesiaLineItems!.filter((x) => !x.markExcluded)
            .forEach((x) => {
              if (x.reconciledCost) {
                subtotal +=
                  x.reconciledAppealType !== AppealType.OverpaidProvider
                    ? +x.reconciledCost
                    : -x.reconciledCost
              }
            })
          break
        case QuoteLineItemType.PT:
          this.props.selectedQuote
            .physicalTherapyLineItems!.filter((x) => !x.markExcluded)
            .forEach((x) => {
              if (x.reconciledCost) {
                subtotal += +x.reconciledCost
              }
            })
          break
        case QuoteLineItemType.MI:
          this.props.selectedQuote
            .miscellaneousLineItems!.filter((x) => !x.markExcluded)
            .forEach((x) => {
              if (x.reconciledCost) {
                subtotal +=
                  x.reconciledAppealType !== AppealType.OverpaidProvider
                    ? +x.reconciledCost
                    : -x.reconciledCost
              }
            })
          break
        default:
          break
      }
    }
    return formatCurrency(subtotal.toString())
  }

  public quoteRef = React.createRef<any>()

  public render() {
    const { getLineItemsTotals, getLineItemsReconciledTotal, selectedQuote } = this.props
    const { isLoading, buttonsLoading } = this.state
    const isReadyToRender = selectedQuote && !isLoading && !buttonsLoading

    return (
      <>
        {!isReadyToRender ? (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 800,
            }}
          >
            <CircularProgress size={100} />
          </div>
        ) : (
          <Formik
            innerRef={this.quoteRef}
            initialValues={createIQuoteFormValues(selectedQuote)}
            onSubmit={() => {}}
            validationSchema={QuoteSchema}
            enableReinitialize={true}
          >
            {({ values, dirty, submitCount }) => (
              <>
                <AddQuoteLineModal reloadParent={this.forceUpdateHandler} />
                <EditQuoteLineModal reloadParent={this.forceUpdateHandler} />
                <ReconcileLineItemModal reloadParent={this.forceUpdateHandler} />
                <ReconcileLineItemWizardModal reloadParent={this.forceUpdateHandler} />
                <Form>
                  {
                    //@ts-ignore
                    (values.adjustedReconciledCost === '' ||
                      //@ts-ignore
                      values.targetReconciledMargin === '') && (
                      <Grid item container justifyContent="flex-end">
                        <Typography
                          style={{
                            paddingRight: '10px',
                            paddingBottom: '10px',
                          }}
                          color="error"
                        >
                          Missing Reconciliation Total Data
                        </Typography>
                      </Grid>
                    )
                  }
                  <Grid container direction="column" spacing={2} wrap="nowrap">
                    <Grid item>
                      {this.props.selectedQuote && (
                        <QuoteDetails
                          disableInvoiceButtons={
                            (dirty || this.state.lineItemDirty) && submitCount === 0
                          }
                          values={values}
                          selectedQuote={this.props.selectedQuote}
                          assistantWarning={this.props.assistantWarning}
                          viewMode={this.state.viewOnly}
                          updateMain={this.forceUpdateHandler}
                          isAdjusterEmailModalOpen={this.props.isAdjusterEmailModalOpen}
                          rerouteToPath={this.props.rerouteToPath}
                          takeoverOfCareDate={this.props.quoteInboxItem?.takeoverOfCare}
                          createInvoice={this.props.createInvoice}
                          quoteInboxItem={this.props.quoteInboxItem}
                          isReferralContactModalOpen={
                            this.props.isReferralContactModalOpen
                          }
                          openReferralContactModal={this.props.openReferralContactModal}
                          openBundleClaimsModal={this.props.openBundleClaimsModal}
                          excludeAllLineItems={this.props.excludeAllLineItems}
                          deleteAllLineItems={this.props.deleteAllLineItems}
                        />
                      )}
                    </Grid>
                    <Grid item>{this.renderPC()}</Grid>
                    <Grid item>{this.renderTC()}</Grid>
                    <Grid item>{this.renderAN()}</Grid>
                    <Grid item>{this.renderPT()}</Grid>
                    <Grid item>{this.renderMI()}</Grid>
                    <Grid container item direction="row" spacing={2}>
                      <Grid item xs={4}>
                        <CardWithTitle title="Notes" icon={Assignment}>
                          <FullWidthField
                            variant="standard"
                            name="note"
                            fullWidth
                            multiline
                            rows="9"
                          />
                        </CardWithTitle>
                      </Grid>
                      <QuoteTotals
                        viewMode={this.state.viewOnly}
                        reconMode={true}
                        getLineItemsTotal={getLineItemsTotals!}
                        getLineItemsReconciledTotal={getLineItemsReconciledTotal!}
                        isConsumerQuote={this.props.selectedQuote?.isConsumer}
                        hasBeenConsumed={this.props.selectedQuote?.hasBeenConsumed}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </>
            )}
          </Formik>
        )}
      </>
    )
  }
}

const InjectedquotesList = inject<
  IStores,
  IReconcileQuoteProps,
  Partial<IReconcileQuoteProps>,
  any
>((stores: IStores) => ({
  getLineItemsTotals: stores.quotes.getLineItemsTotal,
  getLineItemsReconciledTotal: stores.quotes.getLineItemsReconciledTotal,
  getProviderTypes: stores.quotes.getProviderTypes,
  getTypesByCategory: stores.quotes.getTypesByCategory,
  loadQuote: stores.quotes.loadQuote,
  providerTypes: stores.quotes.providerTypes,
  rerouteToPath: stores.global.rerouteToPath,
  selectedQuote: stores.quotes.selectedQuote,
  openReconModal: stores.quotes.openReconcileQuoteLineModal,
  openEditModal: stores.quotes.openEditQuoteLineModal,
  assistantWarning: stores.quotes.assistantWarning,
  isAdjusterEmailModalOpen: stores.quotes.isAdjusterEmailModalOpen,
  isNum: stores.quotes.isNum,
  deleteLineItem: stores.quotes.deleteLineItem,
  excludeLineItem: stores.quotes.excludeLineItem,
  clearReconcileLineItem: stores.quotes.clearReconcileLineItem,
  getSubtotal: stores.quotes.getSubtotal,
  renderLegend: stores.quotes.renderLegend,
  setGetQuoteValuesForStaticAction: stores.quotes.setGetQuoteValuesForStaticAction,
  renderClaimButtons: stores.quotes.renderClaimButtons,
  createInvoice: stores.quotes.createInvoice,
  openReferralContactModal: stores.patientEdit.openReferralContactModal,
  openBundleClaimsModal: stores.quotes.openBillsDialogForSelectedQuote,
  isReferralContactModalOpen: stores.patientEdit.isReferralContactModalOpen,
  setSelectedQuoteUsedItems: stores.quotes.setSelectedQuoteUsedItems,
}))(ReconcileQuote)

const ReconcileQuoteWithRouter = (props: any) => {
  const params = useParams<{ quoteId?: string }>()
  return <InjectedquotesList {...props} match={{ params }} />
}

export default ReconcileQuoteWithRouter
